import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { serverHost } from "variables/serverdata.js";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  Media,
  Container,
  Row,
  Col,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  InputGroup,
  InputGroupText,
  Input,
  FormGroup,
} from "reactstrap";
// core components
import SimpleHeader from "../../../components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

function CompanyClients() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "company_name",
    direction: "asc",
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const visibleColumns = [
    { key: "address", label: "Address" },
    { key: "app_users", label: "Users" },
    { key: "city", label: "City" },
    { key: "company_name", label: "Company Name" },
    { key: "country", label: "Country" },
    { key: "created_at", label: "Created At" },
  ]
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (!currentUser || currentUser.user_role !== "GLOBAL") {
      navigate("/admin/home");
      return;
    }
    fetchClients(currentUser);
  }, []);

  const fetchClients = async () => {
    try {
      // Get current user's dealer_uuid from localStorage
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (!currentUser?.dealer_uuid) {
        console.error("No dealer_uuid found for current user");
        setLoading(false);
        return;
      }

      const all_sites = await fetch(`${serverHost}/fetch_site_count`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Access: `Bearer ${currentUser.access_token}`,
        },
      });
      if (all_sites.status != 200)
        console.error("Error with site count", all_sites.status);
      let siteData = (await all_sites.json()).data;
      console.log("Fetched sites: ", siteData);

      setClients(siteData);
      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    const sortedClients = [...clients].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === "asc" ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
    setClients(sortedClients);
  };

  const toggleDeleteModal = (client = null) => {
    setDeleteModal(!deleteModal);
    setClientToDelete(client);
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;

    try {
      // Delete all sites associated with the client
      console.log("Attempting to delete ", clientToDelete.client_uuid);
      const url = `${serverHost}/delete_client`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          client_uuid: clientToDelete.client_uuid,
        }),
      });

      if (!resp.ok) {
        const errorText = await resp.text(); // get error message from server
        console.error("Failed to send request:", resp.status, errorText);
        return; // optionally stop execution or handle error
      }

      // Remove the deleted client from the state
      setClients(
        clients.filter(
          (client) => client.client_uuid !== clientToDelete.client_uuid,
        ),
      );
      setDeleteModal(false);
      setClientToDelete(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const filteredRows = React.useMemo(() => {
    if (!search) return clients;

    return clients.filter(
      (row) =>
        // AND matches search term
        visibleColumns.some((col) =>
          String(row[col.key] ?? "")
            .toLowerCase()
            .includes(search.toLowerCase())
        )
    );
  }, [clients, search, visibleColumns]);

  const totalPages = Math.max(
    1,
    Math.ceil((filteredRows?.length ?? 0) / itemsPerPage)
  );

  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage]);

  const handleViewClient = (client) => {
    // Store the selected client data in localStorage
    localStorage.setItem(
      "selectedClient",
      JSON.stringify({
        ...client,
        isFromClientsList: true, // Flag to indicate we're coming from clients list
      }),
    );
    // Navigate to the company profile page
    navigate("/admin/companyprofile");
  };

  const handleNewClient = () => {
    // Get current user's dealer_uuid
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (!currentUser?.dealer_uuid) {
      console.error("No dealer_uuid found for current user");
      return;
    }

    // Store an empty client object with isNew flag and dealer_uuid
    localStorage.setItem(
      "selectedClient",
      JSON.stringify({
        company_name: "",
        address: "",
        city: "",
        country: "",
        postal_code: "",
        dealer_uuid: currentUser.dealer_uuid, // Set the dealer_uuid from current user
        isNew: true,
        isFromClientsList: true,
      }),
    );
    // Navigate to the company profile page
    navigate("/admin/companyprofile");
  };

  const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const columns = [
    {
      dataField: "company_name",
      text: "Company Name",
      sort: true,
      headerStyle: { cursor: "pointer" },
      formatter: (cell, client) => (
        <Media className="align-items-center">
          <a
            className="avatar rounded-circle mr-3"
            href="#pablo"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <img
              alt="..."
              src={require("assets/img/theme/building-icon.jpg")}
            />
          </a>
          <Media>
            <span
              className="name mb-0 text-sm"
              style={{
                fontWeight: 600,
                color: isDark ? "rgb(96, 165, 250)" : "rgb(13, 110, 253)",
              }}
            >
              {displayValue(client.company_name)}
            </span>
          </Media>
        </Media>
      ),
    },
    {
      dataField: "address",
      text: "Address",
      sort: true,
      headerStyle: { cursor: "pointer" },
      formatter: displayValue,
    },
    {
      dataField: "city",
      text: "City",
      sort: true,
      headerStyle: { cursor: "pointer" },
      formatter: displayValue,
    },
    {
      dataField: "country",
      text: "Country",
      sort: true,
      headerStyle: { cursor: "pointer" },
      formatter: displayValue,
    },
    {
      dataField: "app_users",
      text: "Users",
      sort: true,
      headerStyle: { cursor: "pointer" },
      formatter: (cell) => (
        <span className="badge badge-dot mr-4">
          <i className="bg-info" />
          {cell || 0} Users
        </span>
      ),
    },
    {
      dataField: "site_count",
      text: "Sites",
      sort: true,
      headerStyle: { cursor: "pointer" },
      formatter: (cell) => (
        <span className="badge badge-dot mr-4">
          <i className="bg-success" />
          {cell || 0} Sites
        </span>
      ),
    },
    {
      dataField: "created_at",
      text: "Created At",
      sort: true,
      headerStyle: { cursor: "pointer" },
      formatter: (cell) =>
        cell ? new Date(cell).toISOString().slice(0, 10) : "N/A",
    },
    {
      dataField: "actions",
      text: "",
      isDummyField: true,
      formatter: (_, client) => (
        <>
          <a
            className="table-action table-action-delete"
            href="#pablo"
            id={`tooltip-delete-${client.client_uuid}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleDeleteModal(client);
            }}
          >
            <i className="fas fa-trash fa-lg" />
          </a>

          <UncontrolledTooltip
            delay={0}
            target={`tooltip-delete-${client.client_uuid}`}
          >
            Delete Client
          </UncontrolledTooltip>
        </>
      ),
    },
  ];

  const rowEvents = {
    onClick: (e, client) => {
      handleViewClient(client);
    },
  };

  return (
    <>
      <SimpleHeader name="Company Clients" parentName="My Account" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
                className="d-flex justify-content-between align-items-center"
              >
                <div className="d-flex align-items-center">
                  <h4 className="mb-0 mr-3">Company Clients</h4>

                  <Form className="navbar-search navbar-search-light form-inline">
                    <FormGroup className="mb-0">
                      <InputGroup className="input-group-alternative input-group-merge search-border">
                        <InputGroupText>
                          <i className="fas fa-search search-icon" />
                        </InputGroupText>
                        <Input
                          className="form-control table-search-bar"
                          placeholder="Search"
                          value={search}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Form>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <div style={{ paddingLeft: "1rem" }}>
                    <Button
                      className="btn-round btn-icon"
                      color="info"
                      onClick={handleNewClient}
                      size="sm"
                    >
                      <span className="btn-inner--icon mr-1">
                        <i className="fas fa-building" />
                      </span>
                      <span className="btn-inner--text">New</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <div className="card-body p-0">
                <div className="table-responsive py-4">
                  <BootstrapTable
                    keyField="client_uuid"
                    data={filteredRows}
                    columns={columns}
                    bootstrap4
                    bordered={false}
                    hover
                    pagination={pagination}
                    rowStyle={{ cursor: "pointer" }}
                    rowEvents={rowEvents}
                    classes="align-items-center table-flush"
                    noDataIndication={() =>
                      loading ? (
                        <div className="text-center">Loading clients...</div>
                      ) : (
                        <div className="text-center">No clients found.</div>
                      )
                    }
                  />
                </div>
              </div>
            </Card>
          </div>
        </Row>
      </Container>
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
        <ModalHeader
          toggle={toggleDeleteModal}
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            color: isDark ? "#e2e8f0" : "#212529",
            borderBottom: "1px solid",
            borderColor: isDark ? "#374151" : "#dee2e6",
          }}
        >
          Delete Client
        </ModalHeader>

        <ModalBody
          style={{
            backgroundColor: isDark ? "#111827" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <div className="text-center">
            <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger" />

            <p className="mb-2">Are you sure you want to delete this client?</p>

            <strong>This action cannot be undone.</strong>

            <p className="text-sm text-black mb-0">
              All client data will be permanently deleted, including:
            </p>

            <ul className="text-left text-sm text-muted mt-3">
              <li>Client information and settings</li>
              <li>All associated sites and their data</li>
              <li>All associated modules and their data</li>
              <li>User access and permissions for this client</li>
              <li>Historical records and logs</li>
            </ul>
          </div>
        </ModalBody>

        <ModalFooter
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            borderTop: "1px solid",
            borderColor: isDark ? "#374151" : "#dee2e6",
          }}
        >
          <Button color="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteClient}>
            Delete Client
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CompanyClients;
